'use client';

import { useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { useCompanyStore } from '@/stores/company-store';

interface CompanySelectorProps {
  className?: string;
}

export function CompanySelector({ className }: CompanySelectorProps) {
  const {
    selectedCompany,
    availableCompanies,
    setSelectedCompany,
    setAvailableCompanies,
  } = useCompanyStore();
  const { isSuperAdmin } = useAuth();
  const { getCompaniesByType, isLoading } = useOrganization();

  // 회사 목록 초기화용 상태
  const [isInitialized, setIsInitialized] = useState(false);

  // 회사 목록 초기화
  useEffect(() => {
    const companies = getCompaniesByType().map(org => ({
      value: org.compCd!,
      label: org.orgName!,
    }));

    setAvailableCompanies(companies);

    // 첫 번째 회사를 기본 선택 (최초 초기화 시에만, 기존 선택이 없는 경우)
    if (!isInitialized && !selectedCompany && companies.length > 0) {
      setSelectedCompany(companies[0].value);
      setIsInitialized(true);
    } else if (companies.length > 0 && !isInitialized) {
      setIsInitialized(true);
    }
  }, [
    getCompaniesByType,
    selectedCompany,
    setAvailableCompanies,
    setSelectedCompany,
    isInitialized,
  ]);

  const handleCompanyChange = (companyCode: string) => {
    setSelectedCompany(companyCode);
    // 회사 변경 시 필터 초기화는 별도 처리
  };

  // 슈퍼어드민이 아니면 렌더링하지 않음
  if (!isSuperAdmin) {
    return null;
  }

  if (isLoading || availableCompanies.length === 0) {
    return (
      <div className={className}>
        <Select disabled>
          <SelectTrigger className='h-8 text-xs'>
            <SelectValue placeholder='회사 로딩 중...' />
          </SelectTrigger>
        </Select>
      </div>
    );
  }

  return (
    <div className={className}>
      <label className='text-xs text-muted-foreground mb-1 block'>
        회사 선택
      </label>
      <Select value={selectedCompany} onValueChange={handleCompanyChange}>
        <SelectTrigger className='h-8 text-xs'>
          <SelectValue placeholder='회사 선택' />
        </SelectTrigger>
        <SelectContent>
          {availableCompanies.map(company => (
            <SelectItem
              key={company.value}
              value={company.value}
              className='text-xs'
            >
              {company.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
